import type { Persona, Project } from '@/utils/pocketbase';
import { extractPersonaName } from '@/utils/markdown';
import { sanitizeHtml } from '@/utils/sanitize';
import { PdfExportButton } from '@/components/shared/PdfExportButton';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';

import { useTranslation } from 'react-i18next';
interface TooltipState {
  id: string;
  type: 'edit' | 'delete' | 'select';
}

interface PersonaDetailPanelProps {
  persona: Persona;
  projects: Project[];
  canEdit: boolean;
  canDelete: boolean;
  tooltipVisible: TooltipState | null;
  onDelete: (personaId: string) => void;
  onEdit: (persona: Persona) => void;
  onTooltipEnter: (state: TooltipState) => void;
  onTooltipLeave: () => void;
}

export function PersonaDetailPanel({
  persona,
  projects,
  canEdit,
  canDelete,
  tooltipVisible,
  onDelete,
  onEdit,
  onTooltipEnter,
  onTooltipLeave,
}: PersonaDetailPanelProps) {
  const { t } = useTranslation('personas');
  const navigate = useLocalizedNavigate();

  const project = projects.find((p) => p.id === persona.project);
  const projectName = project?.name || t('defaultProjectName');
  const personaName = extractPersonaName(persona);
  const personaTitle = `${projectName} - ${personaName}`;

  return (
    <div className="flex-1 min-w-0">
      <div className="bg-[#f4f4f3] rounded-[16px] p-[40px] max-w-[720px] mx-auto w-full">
        {/* Header with avatar and actions */}
        <div className="flex items-start justify-between mb-[40px]">
          <div className="flex gap-[16px] items-center">
            {/* Avatar Icon */}
            <div className="w-[104px] h-[104px] rounded-[16px] bg-[#e0e0e0] flex-shrink-0 flex items-center justify-center">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <path
                  d="M28 28C33.5228 28 38 23.5228 38 18C38 12.4772 33.5228 8 28 8C22.4772 8 18 12.4772 18 18C18 23.5228 22.4772 28 28 28Z"
                  fill="#9ca3af"
                  fillOpacity="0.5"
                />
                <path
                  d="M45 48C45 39.1634 37.3888 32 28 32C18.6112 32 11 39.1634 11 48"
                  stroke="#9ca3af"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d]">
              {personaTitle}
            </h2>
          </div>
          <button
            onClick={() => canDelete && onDelete(persona.id)}
            disabled={!canDelete}
            onMouseEnter={() =>
              !canDelete &&
              onTooltipEnter({ id: 'delete-' + persona.id, type: 'delete' })
            }
            onMouseLeave={onTooltipLeave}
            className={`relative w-[24px] h-[24px] flex-shrink-0 ${!canDelete ? 'cursor-not-allowed' : 'cursor-pointer hover:opacity-70'} transition-opacity`}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 5.98047C17.67 5.65047 14.32 5.48047 10.98 5.48047C9 5.48047 7.02 5.58047 5.04 5.78047L3 5.98047"
                stroke="#565858"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97"
                stroke="#565858"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18.85 9.14L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79C6 22 5.91 20.78 5.8 19.21L5.15 9.14"
                stroke="#565858"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.33 16.5H13.66"
                stroke="#565858"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.5 12.5H14.5"
                stroke="#565858"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            {/* Tooltip for disabled delete */}
            {tooltipVisible?.id === 'delete-' + persona.id && !canDelete && (
              <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                  {t('tooltip.paidOnly')}
                </p>
              </div>
            )}
          </button>
        </div>

        {/* Persona Content */}
        <div className="max-w-none">
          <div
            className="persona-content"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                (persona.generated_text || '')
                  .replace(/```html\n?/gi, '')
                  .replace(/```\n?/g, '')
                  .replace(/^```$/gm, '')
                  .trim()
              ),
            }}
          />
        </div>

        {/* Inline styles for persona content */}
        <style>{`
          .persona-content h3 {
            font-family: 'Manrope', sans-serif;
            font-weight: 600;
            font-size: 20px;
            line-height: 28px;
            color: #0d0d0d;
            margin-bottom: 16px;
            margin-top: 32px;
          }
          .persona-content h3:first-child {
            margin-top: 0;
          }
          .persona-content p {
            font-family: 'Segoe UI', sans-serif;
            font-size: 16px;
            line-height: 24px;
            color: #0d0d0d;
            margin-bottom: 16px;
          }
          .persona-content ul {
            list-style-type: disc;
            padding-left: 24px;
            margin-bottom: 24px;
            font-family: 'Segoe UI', sans-serif;
            font-size: 16px;
            line-height: 24px;
            color: #0d0d0d;
          }
          .persona-content li {
            margin-bottom: 8px;
          }
          .persona-content blockquote {
            border-left: 4px solid #2197f1;
            padding-left: 16px;
            padding-top: 8px;
            padding-bottom: 8px;
            margin: 16px 0;
            font-style: italic;
            font-family: 'Segoe UI', sans-serif;
            font-size: 16px;
            line-height: 24px;
            color: #565858;
          }
          .persona-content strong {
            font-weight: 600;
          }
        `}</style>

        {/* Action Buttons at Bottom */}
        <div className="flex flex-wrap gap-[16px] mt-[40px]">
          <button
            onClick={() => canEdit && onEdit(persona)}
            disabled={!canEdit}
            onMouseEnter={() =>
              !canEdit &&
              onTooltipEnter({ id: 'edit-' + persona.id, type: 'edit' })
            }
            onMouseLeave={onTooltipLeave}
            className={`relative ${
              canEdit
                ? 'bg-[rgba(33,151,241,0.2)] hover:bg-[#2197f1] text-[#2197f1] hover:text-white'
                : 'bg-[#eeeeee] cursor-not-allowed text-[#565858]'
            } rounded-[16px] px-[24px] py-[12px] flex items-center gap-[8px] transition-colors`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M11 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16.04 3.02001L8.16 10.9C7.86 11.2 7.56 11.79 7.5 12.22L7.07 15.23C6.91 16.32 7.68 17.08 8.77 16.93L11.78 16.5C12.2 16.44 12.79 16.14 13.1 15.84L20.98 7.96001C22.34 6.60001 22.98 5.02001 20.98 3.02001C18.98 1.02001 17.4 1.66001 16.04 3.02001Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.91 4.1499C15.58 6.5399 17.45 8.4099 19.85 9.0899"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span
              className="font-segoe font-semibold text-[14px] leading-[20px] text-current"
            >
              {t('detail.editButton')}
            </span>

            {/* Tooltip for disabled edit */}
            {tooltipVisible?.id === 'edit-' + persona.id && !canEdit && (
              <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                  {t('tooltip.paidOnly')}
                </p>
              </div>
            )}
          </button>

          <button
            onClick={() => navigate('/positioning')}
            className="bg-[rgba(33,151,241,0.2)] hover:bg-[#2197f1] text-[#2197f1] hover:text-white rounded-[16px] px-[24px] py-[12px] transition-colors"
          >
            <span className="font-segoe font-semibold text-[14px] leading-[20px] text-current">
              {t('detail.actionPositioning')}
            </span>
          </button>

          <button
            onClick={() => navigate('/research')}
            className="bg-[rgba(33,151,241,0.2)] hover:bg-[#2197f1] text-[#2197f1] hover:text-white rounded-[16px] px-[24px] py-[12px] transition-colors"
          >
            <span className="font-segoe font-semibold text-[14px] leading-[20px] text-current">
              {t('detail.actionResearch')}
            </span>
          </button>

          {persona.generated_text && (
            <PdfExportButton
              data={{
                title: personaTitle,
                content: persona.generated_text,
                artifactType: 'persona',
                artifactTypeLabel: t('detail.pdfLabel'),
                projectName: projectName,
                date: persona.created,
                isHtmlContent: true,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
